import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export interface MeResponse {
  id: string
  email: string
  name: string
  company: string | null
  role: 'admin' | 'manager' | 'user' | null
  allManufacturersAccess: boolean
  allowedManufacturers: string[]
  preferredLocale: 'de' | 'en' | 'ru'
  themePreference: 'light' | 'dark' | 'system'
  taxRatePct: string
  currency: string
}

/**
 * Auth-State: aktueller User, Login-/Logout-Aktionen.
 *
 * CSRF: Spring-Security setzt ein {@code XSRF-TOKEN}-Cookie (lesbar), das wir
 * bei jedem schreibenden Request als {@code X-XSRF-TOKEN}-Header zurueckschicken.
 */
export const useAuthStore = defineStore('auth', () => {
  const me = ref<MeResponse | null>(null)
  const loading = ref(false)

  const isAuthenticated = computed(() => me.value !== null)
  const role = computed(() => me.value?.role ?? null)
  const isAdmin = computed(() => role.value === 'admin')
  const isManager = computed(() => role.value === 'manager' || role.value === 'admin')

  function csrfToken(): string {
    const cookies = document.cookie.split(';')
    for (const c of cookies) {
      const [k, v] = c.trim().split('=')
      if (k === 'XSRF-TOKEN') return decodeURIComponent(v ?? '')
    }
    return ''
  }

  async function fetchMe(): Promise<MeResponse | null> {
    loading.value = true
    try {
      const r = await fetch('/api/me', { credentials: 'include' })
      if (r.status === 401) {
        me.value = null
        return null
      }
      if (!r.ok) throw new Error(`/api/me ${r.status}`)
      me.value = (await r.json()) as MeResponse
      return me.value
    } finally {
      loading.value = false
    }
  }

  async function login(email: string, password: string, remember: boolean): Promise<void> {
    // Erst CSRF-Cookie holen (Spring setzt es auf jedem GET, inkl. /actuator/health)
    if (!csrfToken()) {
      await fetch('/actuator/health', { credentials: 'include' })
    }
    const body = new URLSearchParams({ email, password })
    if (remember) body.append('remember-me', 'true')
    const r = await fetch('/api/auth/login', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-XSRF-TOKEN': csrfToken()
      },
      body
    })
    if (!r.ok) {
      me.value = null
      throw new Error('AUTH_FAILED')
    }
    await fetchMe()
  }

  async function logout(): Promise<void> {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-XSRF-TOKEN': csrfToken() }
    })
    me.value = null
  }

  async function updateLocale(locale: 'de' | 'en' | 'ru'): Promise<void> {
    await fetch('/api/me/locale', {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': csrfToken()
      },
      body: JSON.stringify({ locale })
    })
    if (me.value) me.value.preferredLocale = locale
  }

  async function updateTheme(theme: 'light' | 'dark' | 'system'): Promise<void> {
    await fetch('/api/me/theme', {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': csrfToken()
      },
      body: JSON.stringify({ theme })
    })
    if (me.value) me.value.themePreference = theme
  }

  /** Aktualisiert eigene Stammdaten (Name, E-Mail, Firma). */
  async function updateProfile(input: {
    name: string
    email: string
    company: string | null
  }): Promise<MeResponse> {
    const r = await fetch('/api/me', {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': csrfToken()
      },
      body: JSON.stringify(input)
    })
    if (!r.ok) {
      const body = await r.text()
      throw new ProfileUpdateError(r.status, body)
    }
    const updated = (await r.json()) as MeResponse
    me.value = updated
    return updated
  }

  /**
   * Wechselt das eigene Passwort. Backend antwortet mit 400 +
   * `CURRENT_PASSWORD_WRONG`-Reason, wenn das alte Passwort nicht passt;
   * die UI zeigt dann eine spezifische Fehlermeldung.
   */
  async function changePassword(input: {
    currentPassword: string
    newPassword: string
  }): Promise<void> {
    const r = await fetch('/api/me/password', {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': csrfToken()
      },
      body: JSON.stringify(input)
    })
    if (!r.ok) {
      const body = await r.text()
      throw new ProfileUpdateError(r.status, body)
    }
  }

  return {
    me,
    loading,
    isAuthenticated,
    role,
    isAdmin,
    isManager,
    fetchMe,
    login,
    logout,
    updateLocale,
    updateTheme,
    updateProfile,
    changePassword
  }
})

/** Typed Error fuer Self-Service-Profil-/Passwort-Calls. */
export class ProfileUpdateError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string
  ) {
    super(`profile update failed (${status})`)
  }
}
